import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from 'utils/cn';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

const variantStyles = {
  primary:
    'btn-gold rounded-xl shadow-gold hover:shadow-gold-lg disabled:shadow-none disabled:hover:scale-100 disabled:hover:translate-y-0',
  secondary:
    'rounded-xl border border-border bg-surface-secondary text-content hover:bg-surface-tertiary hover:border-gold/30 transition-all duration-300 ease-in-out',
  ghost:
    'rounded-xl text-content hover:bg-surface-secondary hover:text-accent-strong transition-all duration-250 ease-in-out',
  danger:
    'rounded-xl bg-danger text-danger-foreground hover:bg-danger-hover shadow-subtle transition-all duration-250 ease-in-out',
  outline:
    'rounded-xl border border-border text-content hover:border-gold/40 hover:bg-surface-secondary transition-all duration-250 ease-in-out',
};

const sizeStyles = {
  sm: 'min-h-[44px] h-11 px-3.5 text-sm gap-1.5',
  md: 'min-h-[44px] h-11 px-5 text-sm gap-2',
  lg: 'min-h-12 h-12 px-6 text-base gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading,
      disabled,
      children,
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center font-medium',
        'disabled:opacity-50 disabled:pointer-events-none',
        variant !== 'primary' && 'transition-all duration-300 ease-in-out',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading ? (
        <>
          <span
            className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden
          />
          <span className="sr-only">Loading</span>
        </>
      ) : (
        children
      )}
    </button>
  )
);

Button.displayName = 'Button';
